// ============================================================================
// Matrices EXACTES du noyau (refonte, lot 5.8, moteur v30).
// ============================================================================
// Une ligne de calcul matriciel, écrite en bouts : « AB = [2 × 1 + 3 × 2 ; 1 × 1 + 4 × 2] ».
//
//   - une matrice s'écrit entre crochets, « , » entre deux coefficients d'une ligne,
//     « ; » entre deux lignes : [1, 2 ; 3, 4]. En texte, une virgule DÉCIMALE est
//     collée à ses chiffres (« 0,6 »), la virgule séparatrice est suivie d'une espace ;
//   - un nom de matrice : une majuscule, avec un indice éventuel (A, X₀, U₁, I₂) ou π
//     (π₀) ; deux noms accolés se multiplient (AB = A × B, TX₀, 2A) ;
//   - I (identité, de la taille qu'impose le calcul), I₂, I₃ ; 0 contre une matrice =
//     la matrice nulle ;
//   - puissances en exposant (A², A¹⁰, A⁻¹), transposée Aᵀ, déterminant det(A) ;
//   - moteur v52 : un coefficient, entre parenthèses suivies de ses deux indices :
//     « (M²)₁₃ » (ligne 1, colonne 3 de M²), « (M)₂₂ ».
//
// Les coefficients sont des rationnels EXACTS (0,6 × 100 = 60, jamais 59,999…). Pur,
// sans dépendance hors du noyau.
// ============================================================================

import * as Q from './exact';
import type { Rat } from './exact';

/* ------------------------------ valeurs ------------------------------ */

/** un scalaire, une matrice, ou s·I (identité de taille encore inconnue) */
export type MVal =
  | { t: 's'; q: Rat }
  | { t: 'm'; r: number; c: number; a: Rat[][] }
  | { t: 'id'; s: Rat };

export class MatError extends Error {
  constructor(
    readonly code: 'size' | 'inverse' | 'unknown' | 'div0' | 'power' | 'entry' | 'kind',
    message: string,
  ) {
    super(message);
  }
}

export const scal = (q: Rat): MVal => ({ t: 's', q });
export function mat(a: Rat[][]): MVal {
  return { t: 'm', r: a.length, c: a[0]?.length ?? 0, a };
}
export function identity(n: number, s: Rat = Q.ONE): Rat[][] {
  return Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? s : Q.ZERO)),
  );
}
const sizeText = (v: { r: number; c: number }): string => `${v.r} × ${v.c}`;

/** la matrice d'une valeur, une identité prenant la taille n */
function asMatrix(v: MVal, n: number): Rat[][] {
  if (v.t === 'm') return v.a;
  if (v.t === 'id') return identity(n, v.s);
  throw new MatError('kind', 'Un nombre n’est pas une matrice.');
}

export function addVals(x: MVal, y: MVal, sign: 1 | -1 = 1): MVal {
  const f = (a: Rat, b: Rat) => (sign === 1 ? Q.add(a, b) : Q.sub(a, b));
  if (x.t === 's' && y.t === 's') return scal(f(x.q, y.q));
  if (x.t === 's' || y.t === 's') {
    // « A + 1 » n'a pas de sens (sauf 0, la matrice nulle)
    const s = x.t === 's' ? x : (y as Extract<MVal, { t: 's' }>);
    const o = x.t === 's' ? y : x;
    if (!Q.isZero(s.q)) throw new MatError('kind', 'On n’ajoute pas un nombre à une matrice.');
    return x.t === 's' ? (sign === 1 ? o : scaleVal(o, Q.rat(-1))) : o;
  }
  if (x.t === 'id' && y.t === 'id') return { t: 'id', s: f(x.s, y.s) };
  const n = x.t === 'm' ? x.r : (y as Extract<MVal, { t: 'm' }>).r;
  if (x.t === 'm' && y.t === 'm' && (x.r !== y.r || x.c !== y.c))
    throw new MatError(
      'size',
      `Les tailles ne vont pas : on n’ajoute pas une matrice ${sizeText(x)} et une matrice ${sizeText(y)}.`,
    );
  if ((x.t === 'id' && y.t === 'm' && y.r !== y.c) || (y.t === 'id' && x.t === 'm' && x.r !== x.c))
    throw new MatError('size', 'L’identité est carrée : on ne l’ajoute qu’à une matrice carrée.');
  const A = asMatrix(x, n);
  const B = asMatrix(y, n);
  return mat(A.map((row, i) => row.map((a, j) => f(a, B[i]![j]!))));
}

export function scaleVal(v: MVal, k: Rat): MVal {
  if (v.t === 's') return scal(Q.mul(v.q, k));
  if (v.t === 'id') return { t: 'id', s: Q.mul(v.s, k) };
  return mat(v.a.map((row) => row.map((a) => Q.mul(a, k))));
}

export function mulVals(x: MVal, y: MVal): MVal {
  if (x.t === 's') return scaleVal(y, x.q);
  if (y.t === 's') return scaleVal(x, y.q);
  if (x.t === 'id' && y.t === 'id') return { t: 'id', s: Q.mul(x.s, y.s) };
  if (x.t === 'id') {
    const m = y as Extract<MVal, { t: 'm' }>;
    return scaleVal(m, x.s);
  }
  if (y.t === 'id') return scaleVal(x, y.s);
  if (x.c !== y.r)
    throw new MatError(
      'size',
      `Les tailles ne vont pas : une matrice ${sizeText(x)} fois une matrice ${sizeText(y)} n’existe pas (le nombre de colonnes de la première doit être le nombre de lignes de la seconde).`,
    );
  const out: Rat[][] = [];
  for (let i = 0; i < x.r; i++) {
    const row: Rat[] = [];
    for (let j = 0; j < y.c; j++) {
      let s = Q.ZERO;
      for (let k = 0; k < x.c; k++) s = Q.add(s, Q.mul(x.a[i]![k]!, y.a[k]![j]!));
      row.push(s);
    }
    out.push(row);
  }
  return mat(out);
}

/** déterminant exact (pivot de Gauss) */
export function detOf(a: Rat[][]): Rat {
  const n = a.length;
  const m = a.map((r) => r.slice());
  let det = Q.ONE;
  for (let col = 0; col < n; col++) {
    let p = col;
    while (p < n && Q.isZero(m[p]![col]!)) p++;
    if (p === n) return Q.ZERO;
    if (p !== col) {
      [m[p], m[col]] = [m[col]!, m[p]!];
      det = Q.neg(det);
    }
    const piv = m[col]![col]!;
    det = Q.mul(det, piv);
    for (let r = col + 1; r < n; r++) {
      const f = Q.div(m[r]![col]!, piv)!;
      for (let k = col; k < n; k++) m[r]![k] = Q.sub(m[r]![k]!, Q.mul(f, m[col]![k]!));
    }
  }
  return det;
}

/** inverse exacte (Gauss-Jordan), null si non inversible */
export function inverseOf(a: Rat[][]): Rat[][] | null {
  const n = a.length;
  const m = a.map((r, i) => [...r, ...identity(n)[i]!]);
  for (let col = 0; col < n; col++) {
    let p = col;
    while (p < n && Q.isZero(m[p]![col]!)) p++;
    if (p === n) return null;
    [m[p], m[col]] = [m[col]!, m[p]!];
    const piv = m[col]![col]!;
    m[col] = m[col]!.map((x) => Q.div(x, piv)!);
    for (let r = 0; r < n; r++) {
      if (r === col || Q.isZero(m[r]![col]!)) continue;
      const f = m[r]![col]!;
      m[r] = m[r]!.map((x, k) => Q.sub(x, Q.mul(f, m[col]![k]!)));
    }
  }
  return m.map((r) => r.slice(n));
}

export function powVal(v: MVal, k: number): MVal {
  if (v.t === 's') {
    const q = Q.pow(v.q, k);
    if (!q) throw new MatError('div0', 'Division par 0.');
    return scal(q);
  }
  if (v.t === 'id') {
    const q = Q.pow(v.s, k);
    if (!q) throw new MatError('inverse', 'Cette matrice n’est pas inversible.');
    return { t: 'id', s: q };
  }
  if (v.r !== v.c)
    throw new MatError('size', `Seule une matrice carrée a des puissances (ici ${sizeText(v)}).`);
  let base = v.a;
  if (k < 0) {
    const inv = inverseOf(v.a);
    if (!inv)
      throw new MatError(
        'inverse',
        'Cette matrice n’est pas inversible : son déterminant est nul.',
      );
    base = inv;
    k = -k;
  }
  if (k > 4096) throw new MatError('power', 'Puissance trop grande.');
  let out: MVal = { t: 'id', s: Q.ONE };
  let b: MVal = mat(base);
  // exponentiation rapide
  while (k > 0) {
    if (k & 1) out = mulVals(out, b);
    b = mulVals(b, b);
    k >>= 1;
  }
  return out.t === 'id' ? mat(identity(v.r, out.s)) : out;
}

export function transposeVal(v: MVal): MVal {
  if (v.t !== 'm') return v;
  return mat(Array.from({ length: v.c }, (_, j) => v.a.map((row) => row[j]!)));
}

/** égalité de deux valeurs (une identité prend la taille de l'autre ; 0 = matrice nulle) */
export function valEq(x: MVal, y: MVal): boolean {
  if (x.t === 's' && y.t === 's') return Q.eq(x.q, y.q);
  if (x.t === 's' || y.t === 's') {
    const s = x.t === 's' ? x : (y as Extract<MVal, { t: 's' }>);
    const o = x.t === 's' ? y : x;
    if (!Q.isZero(s.q)) return false;
    if (o.t === 'id') return Q.isZero(o.s);
    return (o as Extract<MVal, { t: 'm' }>).a.every((row) => row.every(Q.isZero));
  }
  if (x.t === 'id' && y.t === 'id') return Q.eq(x.s, y.s);
  if (x.t === 'm' && y.t === 'm')
    return (
      x.r === y.r && x.c === y.c && x.a.every((row, i) => row.every((a, j) => Q.eq(a, y.a[i]![j]!)))
    );
  const m = (x.t === 'm' ? x : y) as Extract<MVal, { t: 'm' }>;
  const id = (x.t === 'id' ? x : y) as Extract<MVal, { t: 'id' }>;
  return m.r === m.c && valEq(m, mat(identity(m.r, id.s)));
}

/* ------------------------------ écriture ------------------------------ */

export type MExpr =
  | { k: 'num'; q: Rat; text: string }
  /** moteur v52 : le coefficient (i, j) d'une matrice (indices à partir de 1) */
  | { k: 'entry'; e: MExpr; i: number; j: number }
  | { k: 'name'; n: string }
  | { k: 'lit'; rows: MExpr[][] }
  | { k: 'add' | 'sub'; l: MExpr; r: MExpr }
  | { k: 'mul'; l: MExpr; r: MExpr; implicit?: boolean }
  | { k: 'div'; l: MExpr; r: MExpr }
  | { k: 'neg'; e: MExpr }
  | { k: 'pow'; e: MExpr; n: number; text: string }
  | { k: 'tr'; e: MExpr }
  | { k: 'det'; e: MExpr }
  | { k: 'paren'; e: MExpr };

export class MatSyntaxError extends Error {}
/** ligne inachevée (l'élève écrit encore) */
export class MatIncomplete extends Error {}

const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const SUB_DIGITS = '₀₁₂₃₄₅₆₇₈₉';
/** nom de matrice : une majuscule (ou π) et un indice éventuel */
export const MATRIX_NAME_RE = new RegExp(`^([A-Z]|π)[${SUB_DIGITS}]*$`);
const SUP_RE = new RegExp(`^(⁻¹|ᵀ|[${SUP_DIGITS}]+)$`);
export const MAT_NUM_RE = /^−?\d+(,\d+)?$/;
const OPS = new Set(['=', '+', '−', '×', '÷', '(', ')', '[', ']', ',', ';']);

export const supToNumber = (s: string): number =>
  Number([...s].map((c) => SUP_DIGITS.indexOf(c)).join(''));
export const numberToSup = (n: number): string =>
  String(n)
    .split('')
    .map((d) => SUP_DIGITS[Number(d)])
    .join('');

export const isSupAtom = (a: string): boolean => SUP_RE.test(a);
/** moteur v52 : les deux indices d'un coefficient, après une parenthèse : « ₁₃ » */
export const ENTRY_RE = new RegExp(`^[${SUB_DIGITS}]+$`);
export const isEntryAtom = (a: string): boolean => ENTRY_RE.test(a);
/** un coefficient écrit dans un texte : « (M²)₁₃ » (une parenthèse fermée, puis des indices) */
export const ENTRY_TEXT_RE = new RegExp(`\\)[${SUB_DIGITS}]`);
export const isMatName = (a: string): boolean => MATRIX_NAME_RE.test(a);

/** Les bouts d'une ligne de calcul matriciel écrite en texte. */
export function matLineAtoms(text: string): string[] {
  const out: string[] = [];
  const s = text.trim();
  let i = 0;
  const map1: Record<string, string> = { '-': '−', '*': '×', '/': '÷', '·': '×' };
  while (i < s.length) {
    const c = s[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/\d/.test(c)) {
      let j = i;
      while (j < s.length && /\d/.test(s[j]!)) j++;
      // virgule décimale : collée aux chiffres
      if (s[j] === ',' && /\d/.test(s[j + 1] ?? '')) {
        j++;
        while (j < s.length && /\d/.test(s[j]!)) j++;
      }
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    // moteur v52 : les indices d'un coefficient, collés à la parenthèse fermante
    if (SUB_DIGITS.includes(c) && out[out.length - 1] === ')') {
      let j = i;
      while (j < s.length && SUB_DIGITS.includes(s[j]!)) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    if (/[A-Z]/.test(c) || c === 'π') {
      let j = i + 1;
      while (j < s.length && SUB_DIGITS.includes(s[j]!)) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    if (s.startsWith('det', i)) {
      out.push('det');
      i += 3;
      continue;
    }
    if (s.startsWith('⁻¹', i)) {
      out.push('⁻¹');
      i += 2;
      continue;
    }
    if (c === 'ᵀ') {
      out.push('ᵀ');
      i++;
      continue;
    }
    if (SUP_DIGITS.includes(c)) {
      let j = i;
      while (j < s.length && SUP_DIGITS.includes(s[j]!)) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    const op = map1[c] ?? c;
    if (!OPS.has(op))
      throw new MatSyntaxError(`« ${c} » ne s’écrit pas dans un calcul de matrices.`);
    out.push(op);
    i++;
  }
  return out;
}

/** l'écriture d'une suite de bouts (« AB = [1, 2 ; 3, 4] ») */
export function matText(atoms: string[]): string {
  let out = '';
  atoms.forEach((a, i) => {
    const prev = atoms[i - 1];
    const glue =
      i === 0 ||
      isSupAtom(a) ||
      isEntryAtom(a) ||
      // un moins devant un nombre : « −1 », « [3, −1 ; …] »
      (prev === '−' && (i < 2 || ['(', '[', ',', ';', '=', '×', '÷'].includes(atoms[i - 2]!))) ||
      a === ')' ||
      a === ']' ||
      a === ',' ||
      prev === '(' ||
      prev === '[' ||
      (prev === 'det' && a === '(') ||
      // AB, 2A, TX₀, A(B + C)
      ((isMatName(a) || a === '(' || a === '[') &&
        prev !== undefined &&
        (isMatName(prev) ||
          isSupAtom(prev) ||
          MAT_NUM_RE.test(prev) ||
          prev === ')' ||
          prev === ']'));
    out += (glue ? '' : ' ') + a;
  });
  return out;
}

/** Lit une ligne : ses membres reliés par « = ». */
export function parseMatLine(A: string[]): MExpr[] {
  let p = 0;
  const peek = () => A[p];
  const eat = (a: string) => {
    if (A[p] !== a) {
      if (p >= A.length) throw new MatIncomplete('');
      throw new MatSyntaxError(`« ${a} » attendu.`);
    }
    p++;
  };
  const startsPrimary = (a: string | undefined): boolean =>
    a !== undefined && (isMatName(a) || a === '[' || a === '(' || a === 'det');

  function expr(): MExpr {
    let l = term();
    while (peek() === '+' || peek() === '−') {
      const op = A[p++]!;
      if (p >= A.length) throw new MatIncomplete('');
      l = { k: op === '+' ? 'add' : 'sub', l, r: term() };
    }
    return l;
  }
  function term(): MExpr {
    let l = unary();
    for (;;) {
      const a = peek();
      if (a === '×' || a === '÷') {
        p++;
        if (p >= A.length) throw new MatIncomplete('');
        const r = unary();
        l = a === '×' ? { k: 'mul', l, r } : { k: 'div', l, r };
      } else if (startsPrimary(a)) {
        l = { k: 'mul', l, r: postfix(), implicit: true };
      } else return l;
    }
  }
  function unary(): MExpr {
    if (peek() === '−') {
      p++;
      if (p >= A.length) throw new MatIncomplete('');
      return { k: 'neg', e: unary() };
    }
    return postfix();
  }
  function postfix(): MExpr {
    let e = primary();
    for (;;) {
      const a = peek();
      // moteur v52 : « (M²)₁₃ », le coefficient (1, 3)
      if (a !== undefined && isEntryAtom(a)) {
        if (e.k !== 'paren')
          throw new MatSyntaxError('Un coefficient s’écrit entre parenthèses : « (M²)₁₃ ».');
        const d = [...a].map((c) => SUB_DIGITS.indexOf(c));
        if (d.length !== 2 || d.some((x) => x < 1))
          throw new MatSyntaxError(
            'Un coefficient a deux indices, la ligne puis la colonne : « (M²)₁₃ ».',
          );
        p++;
        e = { k: 'entry', e: e.e, i: d[0]!, j: d[1]! };
        continue;
      }
      if (a === undefined || !isSupAtom(a)) return e;
      p++;
      if (a === 'ᵀ') e = { k: 'tr', e };
      else if (a === '⁻¹') e = { k: 'pow', e, n: -1, text: a };
      else {
        const n = supToNumber(a);
        if (!Number.isInteger(n) || n < 0) throw new MatSyntaxError('Exposant illisible.');
        e = { k: 'pow', e, n, text: a };
      }
    }
  }
  function primary(): MExpr {
    const a = A[p];
    if (a === undefined) throw new MatIncomplete('');
    if (MAT_NUM_RE.test(a)) {
      p++;
      const q = Q.parseDecimal(a.replace('−', '-'));
      if (!q) throw new MatSyntaxError(`Nombre illisible : « ${a} ».`);
      return { k: 'num', q, text: a };
    }
    if (isMatName(a)) {
      p++;
      return { k: 'name', n: a };
    }
    if (a === 'det') {
      p++;
      eat('(');
      const e = expr();
      eat(')');
      return { k: 'det', e };
    }
    if (a === '(') {
      p++;
      const e = expr();
      eat(')');
      return { k: 'paren', e };
    }
    if (a === '[') {
      p++;
      const rows: MExpr[][] = [[]];
      for (;;) {
        rows[rows.length - 1]!.push(expr());
        const s = A[p];
        if (s === undefined) throw new MatIncomplete('');
        p++;
        if (s === ']') break;
        if (s === ',') continue;
        if (s === ';') {
          rows.push([]);
          continue;
        }
        throw new MatSyntaxError(
          `« ${s} » : dans une matrice, « , » sépare deux coefficients et « ; » deux lignes.`,
        );
      }
      const w = rows[0]!.length;
      if (rows.some((r) => r.length !== w))
        throw new MatSyntaxError(
          'Les lignes de la matrice n’ont pas le même nombre de coefficients.',
        );
      return { k: 'lit', rows };
    }
    throw new MatSyntaxError(`« ${a} » ne commence pas un calcul.`);
  }

  if (!A.length) throw new MatIncomplete('');
  const members: MExpr[] = [expr()];
  while (p < A.length) {
    if (A[p] !== '=') throw new MatSyntaxError(`« ${A[p]} » de trop.`);
    p++;
    if (p >= A.length) throw new MatIncomplete('');
    members.push(expr());
  }
  return members;
}

/* ------------------------------ évaluation ------------------------------ */

export type MatEnv = Map<string, MVal>;

const IDENTITY_RE = new RegExp(`^I([${SUB_DIGITS}]*)$`);

export function evalM(e: MExpr, env: MatEnv): MVal {
  switch (e.k) {
    case 'num':
      return scal(e.q);
    case 'name': {
      const v = env.get(e.n);
      if (v) return v;
      const id = IDENTITY_RE.exec(e.n);
      if (id) {
        if (!id[1]) return { t: 'id', s: Q.ONE };
        const n = Number([...id[1]].map((c) => SUB_DIGITS.indexOf(c)).join(''));
        return mat(identity(n));
      }
      throw new MatError('unknown', `Je ne connais pas la matrice ${e.n}.`);
    }
    case 'lit': {
      const a = e.rows.map((row) =>
        row.map((x) => {
          const v = evalM(x, env);
          if (v.t !== 's') throw new MatError('entry', 'Un coefficient de matrice est un nombre.');
          return v.q;
        }),
      );
      return mat(a);
    }
    case 'paren':
      return evalM(e.e, env);
    case 'add':
      return addVals(evalM(e.l, env), evalM(e.r, env), 1);
    case 'sub':
      return addVals(evalM(e.l, env), evalM(e.r, env), -1);
    case 'neg':
      return scaleVal(evalM(e.e, env), Q.rat(-1));
    case 'mul':
      return mulVals(evalM(e.l, env), evalM(e.r, env));
    case 'div': {
      const r = evalM(e.r, env);
      if (r.t !== 's')
        throw new MatError(
          'kind',
          'On ne divise pas par une matrice : on multiplie par son inverse.',
        );
      const inv = Q.div(Q.ONE, r.q);
      if (!inv) throw new MatError('div0', 'Division par 0.');
      return scaleVal(evalM(e.l, env), inv);
    }
    case 'pow':
      return powVal(evalM(e.e, env), e.n);
    case 'tr':
      return transposeVal(evalM(e.e, env));
    case 'det': {
      const v = evalM(e.e, env);
      if (v.t !== 'm' || v.r !== v.c)
        throw new MatError('size', 'Seule une matrice carrée a un déterminant.');
      return scal(detOf(v.a));
    }
    case 'entry': {
      const v = evalM(e.e, env);
      if (v.t === 's') throw new MatError('kind', 'Un nombre n’a pas de coefficient.');
      if (v.t === 'id') return scal(e.i === e.j ? v.s : Q.ZERO);
      if (e.i > v.r || e.j > v.c)
        throw new MatError(
          'entry',
          `Une matrice ${sizeText(v)} n’a pas de coefficient (${e.i}, ${e.j}).`,
        );
      return scal(v.a[e.i - 1]![e.j - 1]!);
    }
  }
}

/* ------------------------------ forme ------------------------------ */

const ratText = (q: Rat): string => {
  const neg = q.n < 0n;
  const a = neg ? Q.neg(q) : q;
  let t: string;
  if (a.d === 1n) t = a.n.toString();
  else {
    // décimal exact ?
    let d = a.d;
    let k = 0;
    while (d % 10n === 0n) {
      d /= 10n;
      k++;
    }
    let two = 0;
    let five = 0;
    let x = a.d;
    while (x % 2n === 0n) {
      x /= 2n;
      two++;
    }
    while (x % 5n === 0n) {
      x /= 5n;
      five++;
    }
    if (x === 1n) {
      const places = Math.max(two, five);
      const n = (a.n * 10n ** BigInt(places)) / a.d;
      const s = n.toString().padStart(places + 1, '0');
      t = (s.slice(0, s.length - places) + ',' + s.slice(s.length - places)).replace(/,?0+$/, '');
    } else t = `${a.n} ÷ ${a.d}`;
    void k;
  }
  return (neg ? '−' : '') + t;
};
export const matRatText = ratText;

/** écriture canonique d'une expression (comparaisons de forme) */
export function canon(e: MExpr): string {
  switch (e.k) {
    case 'num':
      return ratText(e.q);
    case 'name':
      return e.n;
    case 'lit':
      return '[' + e.rows.map((r) => r.map(canon).join(',')).join(';') + ']';
    case 'paren':
      return canon(e.e);
    case 'add':
      return `(${canon(e.l)}+${canon(e.r)})`;
    case 'sub':
      return `(${canon(e.l)}-${canon(e.r)})`;
    case 'neg':
      return `-(${canon(e.e)})`;
    case 'mul':
      // le produit est associatif : (AB)C et A(BC) s'écrivent pareil
      return '(' + factors(e).map(canon).join('*') + ')';
    case 'div':
      return `(${canon(e.l)}/${canon(e.r)})`;
    case 'pow':
      return `(${canon(e.e)})^${e.n}`;
    case 'tr':
      return `(${canon(e.e)})^T`;
    case 'det':
      return `det(${canon(e.e)})`;
    case 'entry':
      return `(${canon(e.e)})_${e.i},${e.j}`;
  }
}

/** un nombre seul (−2 compris) */
export function numOf(e: MExpr): Rat | null {
  if (e.k === 'num') return e.q;
  if (e.k === 'paren') return numOf(e.e);
  if (e.k === 'neg') {
    const q = numOf(e.e);
    return q ? Q.neg(q) : null;
  }
  return null;
}

/** une matrice écrite avec des nombres seuls */
export function isNumericLit(e: MExpr): boolean {
  const x = e.k === 'paren' ? e.e : e;
  return x.k === 'lit' && x.rows.every((r) => r.every((c) => numOf(c) !== null));
}

/** les noms écrits (I compris) */
export function namesIn(e: MExpr, out = new Set<string>()): Set<string> {
  switch (e.k) {
    case 'name':
      out.add(e.n);
      break;
    case 'lit':
      e.rows.forEach((r) => r.forEach((c) => namesIn(c, out)));
      break;
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
      namesIn(e.l, out);
      namesIn(e.r, out);
      break;
    case 'neg':
    case 'paren':
    case 'pow':
    case 'tr':
    case 'det':
    case 'entry':
      namesIn(e.e, out);
      break;
    default:
      break;
  }
  return out;
}

/** le nombre d'opérations écrites (pour dire qu'un calcul avance) */
export function opCount(e: MExpr): number {
  switch (e.k) {
    case 'num':
    case 'name':
      return 0;
    case 'lit':
      return e.rows.reduce((s, r) => s + r.reduce((t, c) => t + opCount(c), 0), 0);
    case 'paren':
      return opCount(e.e);
    case 'neg':
      return numOf(e) !== null ? 0 : 1 + opCount(e.e);
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
      return 1 + opCount(e.l) + opCount(e.r);
    case 'pow':
    case 'tr':
    case 'det':
      return 1 + opCount(e.e);
    case 'entry':
      return opCount(e.e);
  }
}

const strip = (e: MExpr): MExpr => (e.k === 'paren' ? strip(e.e) : e);

/** les facteurs d'un produit (sans les parenthèses), puissances entières gardées entières */
export function factors(e: MExpr): MExpr[] {
  const x = strip(e);
  if (x.k === 'mul') return [...factors(x.l), ...factors(x.r)];
  return [x];
}

/**
 * Les facteurs MATRICIELS d'un produit, puissances dépliées, regroupés : A × A × B → [(A, 2),
 * (B, 1)] ; A¹⁰¹ et (A²)⁵⁰ × A donnent tous deux [(A, 101)]. null si un facteur n'est ni
 * un nom, ni une matrice écrite, ni une puissance de l'un d'eux.
 */
export function powerFactors(e: MExpr): { base: string; n: number }[] | null {
  const out: { base: string; n: number }[] = [];
  const push = (base: string, n: number) => {
    const last = out[out.length - 1];
    if (last && last.base === base) last.n += n;
    else out.push({ base, n });
  };
  let budget = 2000;
  const single = (y: MExpr): boolean =>
    y.k === 'name' || y.k === 'lit' || (y.k === 'pow' && y.n === -1) || y.k === 'tr';
  const walk = (x: MExpr): boolean => {
    const y = strip(x);
    if (y.k === 'mul') return walk(y.l) && walk(y.r);
    if (y.k === 'pow' && y.n >= 1) {
      const inner = strip(y.e);
      if (single(inner)) {
        push(canon(inner), y.n);
        return true;
      }
      // (AB)² = ABAB, (A²)⁵⁰ = A¹⁰⁰ : on déplie
      for (let i = 0; i < y.n; i++) {
        if (--budget < 0 || !walk(inner)) return false;
      }
      return true;
    }
    if (single(y)) {
      push(canon(y), 1);
      return true;
    }
    return false;
  };
  return walk(e) ? out : null;
}

/** l'expression est-elle une identité (I, I₂, I⁵⁰…) ? */
export function isIdentityExpr(e: MExpr): boolean {
  const x = strip(e);
  if (x.k === 'name') return IDENTITY_RE.test(x.n);
  if (x.k === 'pow') return isIdentityExpr(x.e);
  return false;
}

/** un coefficient écrit : la somme de ses termes, chacun évalué */
export function termValues(e: MExpr): Rat[] | null {
  const out: Rat[] = [];
  const walk = (x: MExpr, sign: 1 | -1): boolean => {
    const y = strip(x);
    if (y.k === 'add') return walk(y.l, sign) && walk(y.r, sign);
    if (y.k === 'sub') return walk(y.l, sign) && walk(y.r, -sign as 1 | -1);
    try {
      const v = evalM(y, new Map());
      if (v.t !== 's') return false;
      out.push(sign === 1 ? v.q : Q.neg(v.q));
      return true;
    } catch {
      return false;
    }
  };
  return walk(e, 1) ? out : null;
}

/** multiset de rationnels (ordre indifférent) */
export function sameMultiset(a: Rat[], b: Rat[]): boolean {
  if (a.length !== b.length) return false;
  const key = (q: Rat) => Q.ratToText(q);
  const m = new Map<string, number>();
  a.forEach((q) => m.set(key(q), (m.get(key(q)) ?? 0) + 1));
  for (const q of b) {
    const n = m.get(key(q)) ?? 0;
    if (!n) return false;
    m.set(key(q), n - 1);
  }
  return true;
}
